import React, { useState, useEffect, useCallback } from 'react';
import { Form, Button, Modal, Row, Col, Card, Spinner } from 'react-bootstrap';
import { Calendar, X, ShoppingCart, DollarSign } from 'lucide-react';
import saleService from '../../services/saleService';

/**
 * ✅ CustomDateFilter V2 — Aperçu + Filtrage côté SERVEUR
 * 
 * Problème V1 : filtrait en local sur allSales (qui = 1 seule page de 10 items)
 * Solution V2 : appelle le serveur pour l'aperçu ET envoie les dates au parent pour le filtrage
 * 
 * Props:
 *   - onFilterChange(filterData) : callback avec {dateMode, selectedDate, startDate, endDate}
 */
const CustomDateFilter = ({ onFilterChange }) => {
  const [showDateModal, setShowDateModal] = useState(false);
  const [dateMode, setDateMode] = useState('single');
  const [selectedDate, setSelectedDate] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isCustomDateActive, setIsCustomDateActive] = useState(false);

  // ✅ NOUVEAU : États pour l'aperçu serveur
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null); // { salesCount, revenue }
  const [previewDebounce, setPreviewDebounce] = useState(null);

  // ✅ NOUVEAU : Charger l'aperçu depuis le serveur quand les dates changent
  const loadPreview = useCallback(async () => {
    let isoStart = null;
    let isoEnd = null;

    if (dateMode === 'single' && selectedDate) {
      const d = new Date(selectedDate);
      isoStart = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0).toISOString();
      isoEnd = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59).toISOString();
    } else if (dateMode === 'range' && startDate && endDate) {
      const sDate = new Date(startDate);
      const eDate = new Date(endDate);
      isoStart = new Date(sDate.getFullYear(), sDate.getMonth(), sDate.getDate(), 0, 0, 0).toISOString();
      isoEnd = new Date(eDate.getFullYear(), eDate.getMonth(), eDate.getDate(), 23, 59, 59).toISOString();
    }

    if (!isoStart || !isoEnd) {
      setPreviewData(null);
      return;
    }

    try {
      setPreviewLoading(true);
      const response = await saleService.getSalesStatsAggregated('all', isoStart, isoEnd);
      
      if (response.success && response.data) {
        const periodStats = response.data.periodStats || {};
        setPreviewData({
          salesCount: periodStats.salesCount ?? 0,
          revenue: periodStats.revenue ?? 0,
        });
      } else {
        setPreviewData(null);
      }
    } catch (error) {
      console.error('❌ Erreur aperçu dates:', error);
      setPreviewData(null);
    } finally {
      setPreviewLoading(false);
    }
  }, [dateMode, selectedDate, startDate, endDate]);

  // ✅ Debounce l'appel serveur quand les dates changent
  useEffect(() => {
    if (!showDateModal) return;

    const hasValidDates = 
      (dateMode === 'single' && selectedDate) ||
      (dateMode === 'range' && startDate && endDate);

    if (!hasValidDates) {
      setPreviewData(null);
      return;
    }

    if (previewDebounce) clearTimeout(previewDebounce);
    const timer = setTimeout(() => {
      loadPreview();
    }, 500); // Attendre 500ms après la dernière saisie
    setPreviewDebounce(timer);

    return () => clearTimeout(timer);
  }, [dateMode, selectedDate, startDate, endDate, showDateModal]);

  // Formater un montant en FCFA
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'decimal',
      minimumFractionDigits: 0,
    }).format(amount || 0);
  };

  // Appliquer le filtre — envoie uniquement les dates au parent
  const handleApplyFilter = () => {
    setIsCustomDateActive(true);
    setShowDateModal(false);

    onFilterChange({
      dateMode,
      selectedDate: dateMode === 'single' ? selectedDate : null,
      startDate: dateMode === 'range' ? startDate : null,
      endDate: dateMode === 'range' ? endDate : null
    });
  };

  // Réinitialiser le filtre
  const handleResetFilter = () => {
    setSelectedDate('');
    setStartDate('');
    setEndDate('');
    setIsCustomDateActive(false);
    setPreviewData(null);
    onFilterChange({
      dateMode: null,
      selectedDate: null,
      startDate: null,
      endDate: null
    });
  };

  // Formater la date pour l'affichage
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Obtenir le label actif
  const getActiveFilterLabel = () => {
    if (!isCustomDateActive) return null;

    if (dateMode === 'single' && selectedDate) {
      return `📅 ${formatDate(selectedDate)}`;
    } else if (dateMode === 'range' && startDate && endDate) {
      return `Du ${formatDate(startDate)} au ${formatDate(endDate)}`;
    }
    return null;
  };

  // Vérifier si les dates sont valides pour activer le bouton Appliquer
  const canApply = 
    (dateMode === 'single' && selectedDate) ||
    (dateMode === 'range' && startDate && endDate);

  return (
    <>
      {/* Bouton pour ouvrir le modal */}
      <Button
        variant={isCustomDateActive ? "success" : "outline-primary"}
        size="sm"
        onClick={() => setShowDateModal(true)}
        className="d-flex align-items-center"
      >
        <Calendar size={16} className="me-1" />
        {isCustomDateActive ? getActiveFilterLabel() : 'Choisir une date'}
      </Button>

      {/* Bouton pour réinitialiser */}
      {isCustomDateActive && (
        <Button
          variant="outline-danger"
          size="sm"
          onClick={handleResetFilter}
          className="ms-1"
        >
          <X size={16} />
        </Button>
      )}

      {/* Modal de sélection de date */}
      <Modal show={showDateModal} onHide={() => setShowDateModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <Calendar size={24} className="me-2" />
            Filtrer par date
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* Choix du mode */}
          <Form.Group className="mb-3">
            <Form.Label>Type de filtre</Form.Label>
            <div>
              <Form.Check
                inline
                type="radio"
                label="Date spécifique"
                name="dateMode"
                checked={dateMode === 'single'}
                onChange={() => { setDateMode('single'); setPreviewData(null); }}
              />
              <Form.Check
                inline
                type="radio"
                label="Plage de dates"
                name="dateMode"
                checked={dateMode === 'range'}
                onChange={() => { setDateMode('range'); setPreviewData(null); }}
              />
            </div>
          </Form.Group>

          {/* Date spécifique */}
          {dateMode === 'single' && (
            <Form.Group className="mb-3">
              <Form.Label>Choisir une date</Form.Label>
              <Form.Control
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
              />
            </Form.Group>
          )}

          {/* Plage de dates */}
          {dateMode === 'range' && (
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Date de début</Form.Label>
                  <Form.Control
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Date de fin</Form.Label>
                  <Form.Control
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    min={startDate}
                  />
                </Form.Group>
              </Col>
            </Row>
          )}

          {/* ✅ NOUVEAU : Aperçu des résultats depuis le SERVEUR */}
          {canApply && (
            <Card bg="light" className="mt-3">
              <Card.Body className="py-2 px-3">
                <div className="text-muted small mb-2 fw-semibold">Aperçu</div>
                
                {previewLoading ? (
                  <div className="text-center py-2">
                    <Spinner animation="border" size="sm" className="me-2" />
                    <small className="text-muted">Chargement...</small>
                  </div>
                ) : previewData ? (
                  <>
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="d-flex align-items-center">
                        <ShoppingCart size={14} className="me-1 text-primary" />
                        <span className="fw-bold small">Ventes trouvées :</span>
                      </span>
                      <span className="badge bg-primary fs-6">
                        {previewData.salesCount}
                      </span>
                    </div>
                    <div className="d-flex justify-content-between align-items-center mt-2">
                      <span className="d-flex align-items-center">
                        <DollarSign size={14} className="me-1 text-success" />
                        <span className="fw-bold small">Chiffre d'affaires :</span>
                      </span>
                      <span className="text-success fw-bold">
                        {formatCurrency(previewData.revenue)} FCFA
                      </span>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-1">
                    <small className="text-muted">
                      Sélectionnez une date pour voir l'aperçu
                    </small>
                  </div>
                )}
              </Card.Body>
            </Card>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDateModal(false)}>
            Annuler
          </Button>
          <Button
            variant="primary"
            onClick={handleApplyFilter}
            disabled={!canApply || previewLoading}
          >
            {previewLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-1" />
                Chargement...
              </>
            ) : (
              <>
                Appliquer le filtre
                {previewData && previewData.salesCount > 0 && (
                  <span className="badge bg-light text-primary ms-2">
                    {previewData.salesCount} vente{previewData.salesCount > 1 ? 's' : ''}
                  </span>
                )}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default CustomDateFilter;